import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { Toaster } from "sonner";
import { api } from "@/lib/api";
import LoginScreen from "@/components/LoginScreen";
import TerminalHeader from "@/components/TerminalHeader";
import ExpiryBanner from "@/components/ExpiryBanner";
import CalcoloPage from "@/pages/CalcoloPage";
import RiepilogoPage from "@/pages/RiepilogoPage";
import MovimentiPage from "@/pages/MovimentiPage";
import StoricoPage from "@/pages/StoricoPage";
import AdminDashboard from "@/pages/AdminDashboard";
import DisclaimerModal from "@/components/DisclaimerModal";
import OnboardingTour from "@/components/OnboardingTour";
import { toast } from "sonner";
import { trySilentResubscribe } from "@/lib/push";

function App() {
  const [auth, setAuth] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("calcolo");
  const [analysis, setAnalysis] = useState(null);
  const [movimentiUnlocked, setMovimentiUnlocked] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [disclaimerView, setDisclaimerView] = useState(false);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    if (auth?.disclaimerAccepted && auth.code && auth.role !== "admin") {
      const key = `zs_onboarded_${auth.code}`;
      if (!localStorage.getItem(key)) setShowTour(true);
    }
  }, [auth?.disclaimerAccepted, auth?.code, auth?.role]);

  const finishTour = useCallback(() => {
    if (auth?.code) localStorage.setItem(`zs_onboarded_${auth.code}`, "1");
    setShowTour(false);
  }, [auth?.code]);

  useEffect(() => {
    const token = localStorage.getItem("zs_token");
    if (!token) return setChecking(false);
    api
      .get("/auth/me")
      .then((r) => setAuth({ role: r.data.role, code: localStorage.getItem("zs_code"), codeExpiresAt: r.data.code_expires_at, disclaimerAccepted: r.data.disclaimer_accepted }))
      .catch(() => {
        localStorage.removeItem("zs_token");
        localStorage.removeItem("zs_code");
      })
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (auth?.disclaimerAccepted) trySilentResubscribe();
  }, [auth?.disclaimerAccepted]);

  const handleLogin = useCallback((data) => {
    localStorage.setItem("zs_token", data.token);
    localStorage.setItem("zs_code", data.code);
    setAuth({ role: data.role, code: data.code, codeExpiresAt: data.code_expires_at, disclaimerAccepted: data.disclaimer_accepted });
  }, []);

  const handleLogout = useCallback(() => {
    api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("zs_token");
    localStorage.removeItem("zs_code");
    setAuth(null);
    setAnalysis(null);
    setMovimentiUnlocked(false);
    setTab("calcolo");
    setAdminOpen(false);
  }, []);

  const handleAnalysis = useCallback((result) => {
    setAnalysis(result);
    setMovimentiUnlocked(false);
    setTab("riepilogo");
  }, []);

  const goToMovimenti = useCallback(() => {
    setMovimentiUnlocked(true);
    setTab("movimenti");
  }, []);

  const acceptDisclaimer = useCallback(async () => {
    try {
      await api.post("/auth/disclaimer/accept");
      setAuth((a) => ({ ...a, disclaimerAccepted: true }));
      toast.success("Informativa accettata. Benvenuto in Zero Signals");
    } catch {
      toast.error("Errore, riprova");
    }
  }, []);

  const rejectDisclaimer = useCallback(() => {
    toast.error("Devi accettare l'informativa per utilizzare Zero Signals");
    handleLogout();
    window.close();
  }, [handleLogout]);

  if (checking) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <span className="font-mono-data text-amber-500/70 text-sm">INIZIALIZZAZIONE TERMINALE…</span>
      </div>
    );
  }

  if (!auth) {
    return (
      <>
        <LoginScreen onLogin={handleLogin} />
        <Toaster theme="dark" position="top-right" richColors />
      </>
    );
  }

  if (!auth.disclaimerAccepted) {
    return (
      <div className="min-h-screen bg-black">
        <DisclaimerModal onAccept={acceptDisclaimer} onReject={rejectDisclaimer} />
        <Toaster theme="dark" position="top-right" richColors />
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-black">
      <TerminalHeader
        auth={auth}
        tab={tab}
        setTab={setTab}
        hasAnalysis={!!analysis}
        movimentiUnlocked={movimentiUnlocked}
        onLogout={handleLogout}
        onOpenAdmin={() => setAdminOpen(true)}
        onOpenDisclaimer={() => setDisclaimerView(true)}
      />
      <ExpiryBanner auth={auth} />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {tab === "calcolo" && <CalcoloPage onAnalysis={handleAnalysis} />}
        {tab === "riepilogo" && <RiepilogoPage analysis={analysis} goToMovimenti={goToMovimenti} goToCalcolo={() => setTab("calcolo")} />}
        {tab === "movimenti" && <MovimentiPage analysis={analysis} goToCalcolo={() => setTab("calcolo")} />}
        {tab === "storico" && <StoricoPage onLoad={handleAnalysis} />}
      </main>
      {auth.role === "admin" && adminOpen && <AdminDashboard onClose={() => setAdminOpen(false)} />}
      {disclaimerView && <DisclaimerModal readOnly onClose={() => setDisclaimerView(false)} />}
      {showTour && <OnboardingTour onDone={finishTour} />}
      <Toaster theme="dark" position="top-right" richColors />
    </div>
  );
}

export default App;
